import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Gem, Award, Heart, Sparkles, Users, Globe } from 'lucide-react';
import Header from './components/Header23';
import Footer from './components/Footer23';

const Template23About = () => {
    const navigate = useNavigate();

    const values = [
        {
            icon: <Gem size={28} />,
            title: 'Artisan Craftsmanship',
            description: 'Every piece is handcrafted by skilled artisans using centuries-old techniques passed down through generations of master weavers and embroiderers.',
        },
        {
            icon: <Award size={28} />,
            title: 'Authentic Heritage',
            description: 'We source the finest fabrics — Banarasi silk, Chanderi, Paithani — directly from weaver communities across India, preserving their cultural legacy.',
        },
        {
            icon: <Heart size={28} />,
            title: 'Celebration First',
            description: 'Every occasion deserves to be special. From intimate mehendi ceremonies to grand wedding receptions, we design for the moments that matter.',
        },
        {
            icon: <Sparkles size={28} />,
            title: 'Modern Traditions',
            description: 'We blend timeless Indian aesthetics with contemporary silhouettes, creating ensembles that honor tradition while embracing modern sensibilities.',
        },
        {
            icon: <Users size={28} />,
            title: 'Community Impact',
            description: 'We employ over 200 artisan families across Rajasthan, Varanasi, and Gujarat, ensuring fair wages and sustainable livelihoods.',
        },
        {
            icon: <Globe size={28} />,
            title: 'Sustainable Fashion',
            description: 'From eco-friendly dyes to biodegradable packaging, every choice we make reflects our commitment to the planet and future generations.',
        },
    ];

    const milestones = [
        { year: '2018', event: 'Founded in Jaipur with a vision to celebrate Indian festive fashion' },
        { year: '2019', event: 'Launched our first bridal collection with 50+ handcrafted pieces' },
        { year: '2020', event: 'Brought our collections online, reaching customers across India' },
        { year: '2022', event: 'Partnered with 200+ artisan families across 3 states' },
        { year: '2024', event: 'Expanded internationally to serve the global Indian diaspora' },
        { year: '2026', event: 'Launched custom-fit technology for perfect celebrations' },
    ];

    return (
        <>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Libre+Baskerville:ital,wght@0,400;0,700;1,400&family=Nunito+Sans:wght@300;400;500;600;700&display=swap');

                .utsav-about-values-grid {
                    display: grid;
                    grid-template-columns: 1fr;
                    gap: 24px;
                }
                @media (min-width: 768px) {
                    .utsav-about-values-grid {
                        grid-template-columns: repeat(2, 1fr);
                    }
                }
                @media (min-width: 1024px) {
                    .utsav-about-values-grid {
                        grid-template-columns: repeat(3, 1fr);
                    }
                }
                .utsav-about-story-layout {
                    display: flex;
                    flex-direction: column;
                    gap: 40px;
                    align-items: center;
                }
                @media (min-width: 768px) {
                    .utsav-about-story-layout {
                        flex-direction: row;
                        gap: 60px;
                    }
                }
                .utsav-about-stats {
                    display: grid;
                    grid-template-columns: repeat(2, 1fr);
                    gap: 24px;
                }
                @media (min-width: 768px) {
                    .utsav-about-stats {
                        grid-template-columns: repeat(4, 1fr);
                    }
                }
            `}</style>

            <div style={{ minHeight: '100vh', backgroundColor: '#FFFDF8' }}>
                <Header cartCount={0} />

                {/* Hero */}
                <div style={{
                    background: 'linear-gradient(135deg, #1B3A2D 0%, #2D5A45 50%, #1B3A2D 100%)',
                    textAlign: 'center',
                    padding: '140px 24px 80px',
                    position: 'relative',
                    overflow: 'hidden',
                }}>
                    <div style={{
                        position: 'absolute',
                        top: 0, left: 0, right: 0, bottom: 0,
                        backgroundImage: `radial-gradient(circle at 30% 70%, rgba(197, 151, 75, 0.1) 0%, transparent 50%)`,
                        pointerEvents: 'none',
                    }} />
                    <div style={{ maxWidth: '700px', margin: '0 auto', position: 'relative', zIndex: 1 }}>
                        <div style={{
                            width: '60px',
                            height: '2px',
                            backgroundColor: '#C5974B',
                            margin: '0 auto 20px',
                        }} />
                        <p style={{
                            fontFamily: "'Nunito Sans', -apple-system, sans-serif",
                            fontSize: '12px',
                            fontWeight: 600,
                            color: '#C5974B',
                            letterSpacing: '4px',
                            textTransform: 'uppercase',
                            margin: '0 0 16px 0',
                        }}>
                            Our Story
                        </p>
                        <h1 style={{
                            fontFamily: "'Libre Baskerville', Georgia, serif",
                            fontSize: 'clamp(32px, 5vw, 52px)',
                            fontWeight: 700,
                            color: '#FFFFFF',
                            margin: '0 0 20px 0',
                            lineHeight: 1.2,
                        }}>
                            Celebrating India's Festive Heritage
                        </h1>
                        <p style={{
                            fontFamily: "'Nunito Sans', -apple-system, sans-serif",
                            fontSize: '17px',
                            color: 'rgba(255,255,255,0.8)',
                            margin: 0,
                            lineHeight: 1.7,
                        }}>
                            Where centuries of artisan craftsmanship meet contemporary celebration wear
                        </p>
                    </div>
                </div>

                {/* Founder Story */}
                <div style={{
                    maxWidth: '1200px',
                    margin: '0 auto',
                    padding: '80px 24px',
                }}>
                    <div className="utsav-about-story-layout">
                        <div style={{ flex: 1 }}>
                            <div style={{
                                borderRadius: '20px',
                                overflow: 'hidden',
                                aspectRatio: '4/5',
                                backgroundColor: '#F8F3EC',
                            }}>
                                <img
                                    src="https://images.pexels.com/photos/7349020/pexels-photo-7349020.jpeg?auto=compress&cs=tinysrgb&w=800"
                                    alt="Our Heritage"
                                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                />
                            </div>
                        </div>
                        <div style={{ flex: 1 }}>
                            <div style={{
                                width: '40px',
                                height: '2px',
                                backgroundColor: '#C5974B',
                                marginBottom: '20px',
                            }} />
                            <h2 style={{
                                fontFamily: "'Libre Baskerville', Georgia, serif",
                                fontSize: 'clamp(24px, 3vw, 36px)',
                                fontWeight: 700,
                                color: '#1B3A2D',
                                margin: '0 0 20px 0',
                                lineHeight: 1.3,
                            }}>
                                A Celebration Born from Love
                            </h2>
                            <p style={{
                                fontFamily: "'Nunito Sans', -apple-system, sans-serif",
                                fontSize: '15px',
                                color: '#5A5A5A',
                                lineHeight: 1.8,
                                margin: '0 0 16px 0',
                            }}>
                                UTSAV was born from a simple yet powerful belief: every celebration deserves to be adorned
                                with the finest Indian craftsmanship. Our founder grew up watching her grandmother's
                                hands transform raw silk into magnificent lehengas for family weddings, each stitch
                                carrying stories of love and tradition.
                            </p>
                            <p style={{
                                fontFamily: "'Nunito Sans', -apple-system, sans-serif",
                                fontSize: '15px',
                                color: '#5A5A5A',
                                lineHeight: 1.8,
                                margin: '0 0 16px 0',
                            }}>
                                Today, UTSAV brings that same devotion to every piece we create. We work
                                directly with artisan communities across Rajasthan, Varanasi, and Gujarat,
                                ensuring that traditional techniques like zardozi, chikankari, and bandhani
                                are not just preserved but celebrated on the global stage.
                            </p>
                            <p style={{
                                fontFamily: "'Libre Baskerville', Georgia, serif",
                                fontSize: '18px',
                                fontStyle: 'italic',
                                color: '#1B3A2D',
                                lineHeight: 1.6,
                                margin: '24px 0 0 0',
                                paddingLeft: '20px',
                                borderLeft: '3px solid #C5974B',
                            }}>
                                "Every thread we weave carries the soul of India's festive spirit."
                            </p>
                        </div>
                    </div>
                </div>

                {/* Stats */}
                <div style={{
                    backgroundColor: '#1B3A2D',
                    padding: '60px 24px',
                }}>
                    <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
                        <div className="utsav-about-stats">
                            {[
                                { number: '200+', label: 'Artisan Families' },
                                { number: '10,000+', label: 'Celebrations Adorned' },
                                { number: '50+', label: 'Unique Techniques' },
                                { number: '15+', label: 'Countries Served' },
                            ].map((stat, idx) => (
                                <div key={idx} style={{ textAlign: 'center' }}>
                                    <p style={{
                                        fontFamily: "'Libre Baskerville', Georgia, serif",
                                        fontSize: 'clamp(28px, 4vw, 40px)',
                                        fontWeight: 700,
                                        color: '#C5974B',
                                        margin: '0 0 4px 0',
                                    }}>
                                        {stat.number}
                                    </p>
                                    <p style={{
                                        fontFamily: "'Nunito Sans', -apple-system, sans-serif",
                                        fontSize: '14px',
                                        fontWeight: 500,
                                        color: 'rgba(255,255,255,0.7)',
                                        margin: 0,
                                        letterSpacing: '1px',
                                    }}>
                                        {stat.label}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Values */}
                <div style={{
                    maxWidth: '1200px',
                    margin: '0 auto',
                    padding: '80px 24px',
                }}>
                    <div style={{ textAlign: 'center', marginBottom: '48px' }}>
                        <div style={{
                            width: '40px',
                            height: '2px',
                            backgroundColor: '#C5974B',
                            margin: '0 auto 16px',
                        }} />
                        <h2 style={{
                            fontFamily: "'Libre Baskerville', Georgia, serif",
                            fontSize: 'clamp(24px, 3vw, 36px)',
                            fontWeight: 700,
                            color: '#1B3A2D',
                            margin: '0 0 12px 0',
                        }}>
                            What We Stand For
                        </h2>
                        <p style={{
                            fontFamily: "'Nunito Sans', -apple-system, sans-serif",
                            fontSize: '16px',
                            color: '#5A5A5A',
                            maxWidth: '500px',
                            margin: '0 auto',
                            lineHeight: 1.6,
                        }}>
                            Our values are woven into every piece we create
                        </p>
                    </div>

                    <div className="utsav-about-values-grid">
                        {values.map((value, idx) => (
                            <div
                                key={idx}
                                style={{
                                    backgroundColor: '#FFFFFF',
                                    borderRadius: '16px',
                                    padding: '32px',
                                    boxShadow: '0 2px 12px rgba(27, 58, 45, 0.06)',
                                    transition: 'all 0.3s ease',
                                    border: '1px solid #E8E0D4',
                                }}
                            >
                                <div style={{
                                    width: '56px',
                                    height: '56px',
                                    borderRadius: '14px',
                                    backgroundColor: '#F8F3EC',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: '#1B3A2D',
                                    marginBottom: '20px',
                                }}>
                                    {value.icon}
                                </div>
                                <h3 style={{
                                    fontFamily: "'Libre Baskerville', Georgia, serif",
                                    fontSize: '18px',
                                    fontWeight: 700,
                                    color: '#1B3A2D',
                                    margin: '0 0 10px 0',
                                }}>
                                    {value.title}
                                </h3>
                                <p style={{
                                    fontFamily: "'Nunito Sans', -apple-system, sans-serif",
                                    fontSize: '14px',
                                    color: '#5A5A5A',
                                    lineHeight: 1.7,
                                    margin: 0,
                                }}>
                                    {value.description}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Timeline */}
                <div style={{
                    backgroundColor: '#F8F3EC',
                    padding: '80px 24px',
                }}>
                    <div style={{ maxWidth: '700px', margin: '0 auto' }}>
                        <div style={{ textAlign: 'center', marginBottom: '48px' }}>
                            <div style={{
                                width: '40px',
                                height: '2px',
                                backgroundColor: '#C5974B',
                                margin: '0 auto 16px',
                            }} />
                            <h2 style={{
                                fontFamily: "'Libre Baskerville', Georgia, serif",
                                fontSize: 'clamp(24px, 3vw, 36px)',
                                fontWeight: 700,
                                color: '#1B3A2D',
                                margin: 0,
                            }}>
                                Our Journey
                            </h2>
                        </div>

                        <div style={{ position: 'relative' }}>
                            {/* Timeline Line */}
                            <div style={{
                                position: 'absolute',
                                left: '20px',
                                top: 0,
                                bottom: 0,
                                width: '2px',
                                backgroundColor: '#E8E0D4',
                            }} />

                            {milestones.map((milestone, idx) => (
                                <div
                                    key={idx}
                                    style={{
                                        display: 'flex',
                                        gap: '24px',
                                        marginBottom: idx < milestones.length - 1 ? '32px' : 0,
                                        position: 'relative',
                                    }}
                                >
                                    <div style={{
                                        width: '42px',
                                        height: '42px',
                                        borderRadius: '50%',
                                        backgroundColor: '#C5974B',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        flexShrink: 0,
                                        zIndex: 1,
                                    }}>
                                        <span style={{
                                            fontFamily: "'Nunito Sans', -apple-system, sans-serif",
                                            fontSize: '11px',
                                            fontWeight: 700,
                                            color: '#FFFFFF',
                                        }}>
                                            {milestone.year.slice(-2)}
                                        </span>
                                    </div>
                                    <div style={{
                                        backgroundColor: '#FFFFFF',
                                        borderRadius: '12px',
                                        padding: '16px 20px',
                                        flex: 1,
                                        boxShadow: '0 2px 8px rgba(27, 58, 45, 0.04)',
                                    }}>
                                        <span style={{
                                            fontFamily: "'Nunito Sans', -apple-system, sans-serif",
                                            fontSize: '12px',
                                            fontWeight: 700,
                                            color: '#C5974B',
                                            letterSpacing: '1px',
                                        }}>
                                            {milestone.year}
                                        </span>
                                        <p style={{
                                            fontFamily: "'Nunito Sans', -apple-system, sans-serif",
                                            fontSize: '14px',
                                            color: '#1B3A2D',
                                            lineHeight: 1.6,
                                            margin: '4px 0 0 0',
                                        }}>
                                            {milestone.event}
                                        </p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* CTA */}
                <div style={{
                    textAlign: 'center',
                    padding: '80px 24px',
                    maxWidth: '600px',
                    margin: '0 auto',
                }}>
                    <h2 style={{
                        fontFamily: "'Libre Baskerville', Georgia, serif",
                        fontSize: 'clamp(24px, 3vw, 32px)',
                        fontWeight: 700,
                        color: '#1B3A2D',
                        margin: '0 0 16px 0',
                    }}>
                        Begin Your Celebration
                    </h2>
                    <p style={{
                        fontFamily: "'Nunito Sans', -apple-system, sans-serif",
                        fontSize: '15px',
                        color: '#5A5A5A',
                        lineHeight: 1.6,
                        margin: '0 0 32px 0',
                    }}>
                        Explore our handcrafted collections designed for every festive moment
                    </p>
                    <button
                        onClick={() => navigate('/template23/shop')}
                        style={{
                            padding: '16px 48px',
                            backgroundColor: '#C5974B',
                            color: '#FFFFFF',
                            border: 'none',
                            borderRadius: '10px',
                            fontFamily: "'Nunito Sans', -apple-system, sans-serif",
                            fontSize: '14px',
                            fontWeight: 700,
                            letterSpacing: '1px',
                            textTransform: 'uppercase',
                            cursor: 'pointer',
                            transition: 'all 0.3s ease',
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#B8873F'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#C5974B'; }}
                    >
                        Shop Collection
                    </button>
                </div>

                <Footer />
            </div>
        </>
    );
};

export default Template23About;
